/**
 * Scheduled withdrawal (SWP) and scheduled transfer (STP) plans, on an
 * investor's behalf.
 *
 * The two are the same machine with different nouns — same folio eligibility
 * check, same create → verify → sync sequence, same update and cancel, same
 * plan record shape. The user platform keeps them in two files
 * (`helpers/mfRedemptionPlans.ts` and `helpers/mfSwitchPlans.ts`) that are
 * near-identical; here they share one implementation parameterised by kind, so
 * a fix to the lifecycle cannot land on one and miss the other.
 *
 * Endpoints are exactly the user platform's:
 *
 *   GET   mutual_fund/folio_number/check/            eligible folios (both kinds)
 *   POST  mutual_fund/mf_{redemption,switch}_plans/create/
 *   GET   .../get/{id}/        .../list/        .../list/sync/
 *   PATCH .../update/          POST .../cancel/{id}/
 *
 * NEITHER NEEDS A MANDATE OR A PAYMENT. A withdrawal or a transfer moves units
 * the investor already holds, so there is no bank authorization step — which is
 * why these have no popup flow, unlike a lumpsum or a SIP.
 *
 * NO CONSENT OTP: the admin acts directly on the investor's behalf.
 */

import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
  makePatchRequestOnUserService,
} from "./api";
import type { MFRecord, MFSchemeDetails } from "./mfAdminInvestments";

export type MFPlanKind = "swp" | "stp";

/** Per-frequency limits a scheme publishes for SWP/STP. */
export interface MFPlanFrequencyRule {
  frequency: string;
  dates: number[];
  minAmount: number | null;
  maxAmount: number | null;
  amountMultiple: number | null;
  minInstallments: number | null;
}

/** A row of `folio_number/check/`. */
export interface MFFolioEligibility {
  folio_number: string;
  type?: string;
  scheme: string;
  purchase_id?: string | null;
  purchase_plan_id?: string | null;
  scheme_details: MFRecord;
}

/** The source a plan draws from — one folio, reached by one reference. */
export interface MFPlanSource {
  eligibility: MFFolioEligibility;
  referenceField: "purchase_id" | "purchase_plan_id";
  referenceId: string;
  folioNumber: string;
  schemeIsin: string;
  fundName: string;
}

/** Shared plan record. `redemption_plan_id` on SWP, `switch_plan_id` on STP. */
export interface MFScheduledPlan extends MFRecord {
  uuid?: string;
  redemption_plan_id?: string;
  switch_plan_id?: string;
  id?: string;
  state?: string | null;
  webhook_status?: string | null;
  systematic?: boolean;
  mf_investment_account?: string | null;
  folio_number?: string | null;
  scheme?: string | null;
  amount?: number | string | null;
  frequency?: string | null;
  installment_day?: number | string | null;
  number_of_installments?: number | null;
  remaining_installments?: number | null;
  next_installment_date?: string | null;
  cancellation_scheduled_on?: string | null;
  reason?: string | null;
  failed_reason?: string | null;
  failure_code?: string | null;
  created_at?: string | null;
  inserted_at?: string | null;
  updated_at?: string | null;
}

interface KindConfig {
  base: string;
  idField: "redemption_plan_id" | "switch_plan_id";
  frequencyBlock: "swp_frequency_specific_data" | "stp_frequency_specific_data";
  noun: string;
}

const CONFIG: Record<MFPlanKind, KindConfig> = {
  swp: {
    base: "/mutual_fund/mf_redemption_plans",
    idField: "redemption_plan_id",
    frequencyBlock: "swp_frequency_specific_data",
    noun: "SWP",
  },
  stp: {
    base: "/mutual_fund/mf_switch_plans",
    idField: "switch_plan_id",
    frequencyBlock: "stp_frequency_specific_data",
    noun: "STP",
  },
};

/**
 * FP creates these asynchronously. The user platform waits 2s before verifying
 * and again before syncing; shortening it makes `get` answer with a plan that
 * has not been activated yet.
 */
const STEP_DELAY_MS = 2000;

const waitForNextStep = () =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, STEP_DELAY_MS);
  });

const isRecord = (value: any): value is MFRecord =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** Digs the human-readable reason out of whatever shape the error took. */
const messageFrom = (payload: any): string => {
  if (typeof payload === "string") {
    const message = payload.trim();
    // A gateway that answers with an HTML error page has nothing to quote.
    if (/<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]/i.test(message)) {
      return "";
    }
    return message;
  }
  if (Array.isArray(payload)) {
    return payload.map(messageFrom).filter(Boolean).join(" ");
  }
  if (!isRecord(payload)) return "";
  const keys = [
    "non_field_errors",
    "reason",
    "failed_reason",
    "failure_code",
    "detail",
    "message",
    "error",
    "errors",
  ];
  for (const key of keys) {
    if (!(key in payload)) continue;
    const message = messageFrom(payload[key]);
    if (message) return message;
  }
  return "";
};

export const getMFPlanError = (error: any, fallback: string): string => {
  const fromResponse = messageFrom(error?.response?.data);
  if (fromResponse) return fromResponse;
  return error?.message || fallback;
};

const assertSuccess = (payload: any, fallback: string) => {
  if (isRecord(payload) && payload.success === false) {
    throw new Error(messageFrom(payload) || fallback);
  }
};

const dataRecord = (payload: any): MFRecord | null => {
  if (!isRecord(payload)) return null;
  return isRecord(payload.data) ? payload.data : payload;
};

const normalizePlan = (payload: any, noun: string): MFScheduledPlan => {
  const record = dataRecord(payload);
  if (!record) throw new Error(`The ${noun} response did not contain plan data.`);
  return record as MFScheduledPlan;
};

export const getMFPlanId = (kind: MFPlanKind, plan: MFScheduledPlan): string =>
  String(plan[CONFIG[kind].idField] || plan.id || "").trim();

const listFrom = (payload: any): MFScheduledPlan[] => {
  if (!isRecord(payload)) return [];
  const data = payload.data;
  const candidates = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.results)
    ? data.results
    : isRecord(data) && Array.isArray(data.plans)
    ? data.plans
    : isRecord(data) && Array.isArray(data.redemption_plans)
    ? data.redemption_plans
    : isRecord(data) && Array.isArray(data.switch_plans)
    ? data.switch_plans
    : Array.isArray(payload.results)
    ? payload.results
    : [];
  return candidates.filter(isRecord) as MFScheduledPlan[];
};

const planTimestamp = (plan: MFScheduledPlan) => {
  const timestamp = new Date(
    plan.created_at || plan.inserted_at || plan.updated_at || 0
  ).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/* ─────────────────────────── scheme rules ──────────────────────────── */

/**
 * The per-frequency limits for this kind.
 *
 * The SWP and STP blocks do NOT carry the same frequencies as each other or as
 * SIP — a scheme names only what it supports — so never infer one from another.
 */
export const getMFPlanFrequencyRules = (
  kind: MFPlanKind,
  schemeDetails: MFRecord | null | undefined
): MFPlanFrequencyRule[] => {
  const block = (schemeDetails || {})[CONFIG[kind].frequencyBlock];
  if (!isRecord(block)) return [];
  return Object.keys(block)
    .filter((frequency) => isRecord(block[frequency]))
    .map((frequency) => {
      const rule = block[frequency] as MFRecord;
      const number = (value: any): number | null => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      };
      return {
        frequency: frequency.trim().toLowerCase(),
        dates: Array.isArray(rule.dates)
          ? rule.dates.filter((date: any) => Number.isInteger(date))
          : [],
        minAmount: number(rule.min_installment_amount),
        maxAmount: number(rule.max_installment_amount),
        amountMultiple: number(rule.amount_multiples),
        minInstallments: number(rule.min_installments),
      };
    });
};

/** Frequency rules straight off a `get_schemes_by_isin` response. */
export const getMFPlanFrequencyRulesFromScheme = (
  kind: MFPlanKind,
  details: MFSchemeDetails | null | undefined
): MFPlanFrequencyRule[] =>
  getMFPlanFrequencyRules(kind, details ? (details.data as MFRecord) : null);

/* ───────────────────────── folio eligibility ───────────────────────── */

/**
 * Which of these folios can carry a scheduled plan, and under what reference.
 *
 * One call for both kinds — the endpoint is product-neutral. A folio comes back
 * with either a `purchase_id` or a `purchase_plan_id`, and that is the field
 * the create payload has to key on.
 */
export const checkMFPlanFolios = async (
  folioNumbers: string[]
): Promise<MFFolioEligibility[]> => {
  const folios = folioNumbers.map((folio) => String(folio || "").trim()).filter(Boolean);
  if (!folios.length) return [];
  const response: any = await makeGETRequestOnUserService(
    `/mutual_fund/folio_number/check/?folio_numbers=${encodeURIComponent(folios.join(","))}`
  );
  assertSuccess(response?.data, "Folio eligibility could not be checked.");
  const data = isRecord(response?.data) ? response.data.data : null;
  if (!Array.isArray(data)) {
    throw new Error("The folio eligibility response was not in the expected format.");
  }
  return data.filter(
    (entry: any) =>
      isRecord(entry) &&
      typeof entry.folio_number === "string" &&
      typeof entry.scheme === "string" &&
      isRecord(entry.scheme_details)
  ) as MFFolioEligibility[];
};

/** Turns an eligible folio into the source a create payload keys on. */
export const toMFPlanSource = (
  eligibility: MFFolioEligibility,
  fundName: string
): MFPlanSource | null => {
  const purchasePlanId = String(eligibility.purchase_plan_id || "").trim();
  const purchaseId = String(eligibility.purchase_id || "").trim();
  if (purchasePlanId) {
    return {
      eligibility,
      referenceField: "purchase_plan_id",
      referenceId: purchasePlanId,
      folioNumber: eligibility.folio_number,
      schemeIsin: eligibility.scheme,
      fundName,
    };
  }
  if (purchaseId) {
    return {
      eligibility,
      referenceField: "purchase_id",
      referenceId: purchaseId,
      folioNumber: eligibility.folio_number,
      schemeIsin: eligibility.scheme,
      fundName,
    };
  }
  return null;
};

/* ──────────────────────────── plan lifecycle ───────────────────────── */

export const fetchMFPlans = async (
  kind: MFPlanKind,
  userUuid: string
): Promise<MFScheduledPlan[]> => {
  try {
    const response: any = await makeGETRequestOnUserService(
      `${CONFIG[kind].base}/list/?user_uuid=${encodeURIComponent(userUuid)}`
    );
    assertSuccess(response?.data, `${CONFIG[kind].noun} plans could not be loaded.`);
    return listFrom(response?.data).sort(
      (left, right) => planTimestamp(right) - planTimestamp(left)
    );
  } catch (error: any) {
    // No plans yet reads as a 404 here, which is not a failure to report.
    if (error?.response?.status === 404) return [];
    throw error;
  }
};

export const fetchMFPlan = async (
  kind: MFPlanKind,
  planId: string
): Promise<MFScheduledPlan> => {
  const config = CONFIG[kind];
  const response: any = await makeGETRequestOnUserService(
    `${config.base}/get/${encodeURIComponent(planId)}/`
  );
  assertSuccess(response?.data, `The ${config.noun} plan could not be verified.`);
  const plan = normalizePlan(response?.data, config.noun);
  if (getMFPlanId(kind, plan) !== planId) {
    throw new Error(`The verified ${config.noun} plan did not match the created plan.`);
  }
  return plan;
};

export const syncMFPlans = async (
  kind: MFPlanKind,
  mfInvestmentAccount: string
): Promise<void> => {
  const config = CONFIG[kind];
  const response: any = await makeGETRequestOnUserService(
    `${config.base}/list/sync/?mf_investment_account=${encodeURIComponent(mfInvestmentAccount)}`
  );
  assertSuccess(response?.data, `The ${config.noun} plan could not be synchronized.`);
};

const requireActivePlan = (
  kind: MFPlanKind,
  plan: MFScheduledPlan
): MFScheduledPlan => {
  if (String(plan.state || "").trim().toLowerCase() !== "active") {
    throw new Error(
      String(plan.reason || "").trim() ||
        String(plan.failed_reason || "").trim() ||
        String(plan.failure_code || "").trim() ||
        `The ${CONFIG[kind].noun} plan is ${plan.state || "not active"}.`
    );
  }
  return plan;
};

/**
 * Create is not the end of it: the plan has to come back active and its
 * investment account has to be synchronised, or the plan exists at FP and
 * nowhere else.
 */
const verifyAndSync = async (
  kind: MFPlanKind,
  planId: string
): Promise<MFScheduledPlan> => {
  await waitForNextStep();
  const plan = requireActivePlan(kind, await fetchMFPlan(kind, planId));
  const account = String(plan.mf_investment_account || "").trim();
  if (!account) {
    throw new Error(
      `The active ${CONFIG[kind].noun} response did not include an investment account.`
    );
  }
  await waitForNextStep();
  await syncMFPlans(kind, account);
  return plan;
};

export interface CreateMFPlanInput {
  source: MFPlanSource;
  amount: number;
  frequency: string;
  numberOfInstallments: number;
  installmentDay?: number;
  /** STP only — where the units go. */
  destinationIsin?: string;
}

export const createMFPlan = async (
  kind: MFPlanKind,
  input: CreateMFPlanInput
): Promise<MFScheduledPlan> => {
  const config = CONFIG[kind];
  const body: MFRecord = {
    [input.source.referenceField]: input.source.referenceId,
    amount: input.amount,
    frequency: input.frequency,
    systematic: true,
    number_of_installments: input.numberOfInstallments,
    auto_generate_installments: true,
  };
  if (input.installmentDay != null) body.installment_day = input.installmentDay;
  if (kind === "stp") {
    if (!input.destinationIsin) {
      throw new Error("An STP needs a destination scheme.");
    }
    body.isin_source = input.source.schemeIsin;
    body.isin_destination = input.destinationIsin;
  }

  const response: any = await makePOSTRequestOnUserService(
    `${config.base}/create/`,
    body
  );
  assertSuccess(response?.data, `The ${config.noun} plan could not be created.`);
  const created = normalizePlan(response?.data, config.noun);
  const planId = getMFPlanId(kind, created);
  if (!planId) {
    throw new Error(`The ${config.noun} create response did not include a plan ID.`);
  }
  return verifyAndSync(kind, planId);
};

/** Only the amount is editable, on both kinds. */
export const updateMFPlanAmount = async (
  kind: MFPlanKind,
  plan: MFScheduledPlan,
  amount: number
): Promise<MFScheduledPlan> => {
  const config = CONFIG[kind];
  const planId = getMFPlanId(kind, plan);
  if (!planId) throw new Error(`The ${config.noun} plan ID is unavailable.`);
  const response: any = await makePatchRequestOnUserService(`${config.base}/update/`, {
    [config.idField]: planId,
    amount,
  });
  assertSuccess(response?.data, `The ${config.noun} plan could not be updated.`);
  return verifyAndSync(kind, planId);
};

export const cancelMFPlan = async (
  kind: MFPlanKind,
  plan: MFScheduledPlan
): Promise<MFScheduledPlan> => {
  const config = CONFIG[kind];
  const planId = getMFPlanId(kind, plan);
  if (!planId) throw new Error(`The ${config.noun} plan ID is unavailable.`);
  const response: any = await makePOSTRequestOnUserService(
    `${config.base}/cancel/${encodeURIComponent(planId)}/`,
    {}
  );
  assertSuccess(response?.data, `The ${config.noun} plan could not be cancelled.`);
  const cancelled = normalizePlan(response?.data, config.noun);
  const returnedId = getMFPlanId(kind, cancelled);
  if (returnedId && returnedId !== planId) {
    throw new Error(`The cancelled ${config.noun} plan did not match the selected plan.`);
  }
  return cancelled;
};

export const canCancelMFPlan = (plan: MFScheduledPlan): boolean => {
  if (plan.cancellation_scheduled_on) return false;
  const state = String(plan.state || plan.webhook_status || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  return !["cancelled", "completed", "failed", "rejected", "reversed"].some(
    (value) => state.indexOf(value) >= 0
  );
};
